import React from "react";
import { format_date, toFixed } from "../CustomComponent";
import { blockStyle, cardStyle, cellMeta, toThaiYear } from "./constants";

const Stat = ({ label, value, color, hint }) => (
  <div className="col-6 col-lg-3">
    <div className="rounded-4 p-3 h-100" style={cardStyle}>
      <div className="small text-secondary mb-1">{label}</div>
      <div className="fw-bold" style={{ color, fontSize: "clamp(0.95rem, 2vw, 1.25rem)" }}>{value}</div>
      {hint ? <div className="text-secondary" style={{ fontSize: "0.72rem" }}>{hint}</div> : null}
    </div>
  </div>
);

const FilingCard = ({ cell, onOpen }) => {
  const meta = cellMeta(cell);
  return (
    <div className="col-12 col-xl-6">
      <div className="p-3 p-md-4 h-100" style={blockStyle}>
        <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
          <div>
            <h6 className="fw-semibold mb-1">{cell.label} · {cell.full_label}</h6>
            <div className="small text-secondary">{cell.hint}</div>
          </div>
          <span className="rounded-pill px-3 py-1 small fw-semibold text-nowrap" style={{ background: meta.background, color: meta.color }}>
            {meta.label}
          </span>
        </div>

        <div className="d-flex justify-content-between py-2 border-bottom">
          <span className="text-secondary">ฐานภาษี (กำไรสุทธิ)</span>
          <span className="fw-semibold">{toFixed(cell.base)}</span>
        </div>
        <div className="d-flex justify-content-between py-2 border-bottom">
          <span className="text-secondary">ภาษีที่ต้องชำระ</span>
          <span className="fw-bold" style={{ color: "#b63636" }}>{toFixed(cell.tax)}</span>
        </div>
        <div className="d-flex justify-content-between py-2 border-bottom">
          <span className="text-secondary">กำหนดยื่น</span>
          <span className="fw-semibold">
            {cell.due_date ? format_date(cell.due_date, "dd/mm/yyyy") : "-"}
            <span className="text-secondary small"> (ออนไลน์ถึง {cell.efiling_due_date ? format_date(cell.efiling_due_date, "dd/mm/yyyy") : "-"})</span>
          </span>
        </div>
        {cell.filed_date ? (
          <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-secondary">ยื่นเมื่อ</span>
            <span className="fw-semibold">{format_date(cell.filed_date, "dd/mm/yyyy")}</span>
          </div>
        ) : null}

        <button className="btn btn-outline-primary rounded-3 mt-3" onClick={() => onOpen(cell)}>
          <i className="fa-solid fa-pen me-2" />บันทึกยอดที่ยื่นจริง
        </button>
      </div>
    </div>
  );
};

// ภาษีเงินได้นิติบุคคล: กำไรสุทธิของปี อัตราขั้นบันได SME และแบบ ภ.ง.ด.51 / ภ.ง.ด.50
export default function CorporateTaxPanel({ annual, year, onOpenCell }) {
  if (!annual) return null;
  const brackets = annual.cit?.brackets || [];

  return (
    <>
      <div className="row g-3 mb-4">
        <Stat label="รายได้ทั้งปี (ไม่รวม VAT)" value={toFixed(annual.revenue)} color="#0c76e5" />
        <Stat label="ค่าใช้จ่าย" value={toFixed(annual.expense)} color="#12a5b6" hint="ไม่รวมต้นทุนพนักงาน" />
        <Stat label="ต้นทุนพนักงาน" value={toFixed(annual.payroll_cost)} color="#7a5ad6" hint="เงินเดือน + สมทบนายจ้าง" />
        <Stat label="กำไรสุทธิทางบัญชี" value={toFixed(annual.net_profit)} color={annual.net_profit >= 0 ? "#188251" : "#b63636"} />
      </div>

      <div className="p-3 p-md-4 mb-4" style={blockStyle}>
        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
          <h6 className="fw-semibold mb-0">การคำนวณภาษีนิติบุคคล ปี {toThaiYear(year)}</h6>
          <span className="rounded-pill px-3 py-1 small fw-semibold" style={{ background: "rgba(12, 118, 229, 0.10)", color: "#0b4f84" }}>
            {annual.cit?.is_sme ? "ใช้อัตรา SME (รายได้ไม่เกิน 30 ล้าน)" : "ใช้อัตราปกติ 20%"}
          </span>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium">ขั้นกำไรสุทธิ (บาท)</th>
                <th className="text-secondary fw-medium text-end">อัตรา</th>
                <th className="text-secondary fw-medium text-end text-nowrap">กำไรในขั้นนี้</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ภาษีขั้นนี้</th>
              </tr>
            </thead>
            <tbody>
              {brackets.map((row) => (
                <tr key={`${row.min}-${row.rate}`}>
                  <td className="text-nowrap">
                    {toFixed(row.min, 0)} - {row.max === null ? "ขึ้นไป" : toFixed(row.max, 0)}
                  </td>
                  <td className="text-end">{row.rate}%</td>
                  <td className="text-end">{toFixed(row.amount)}</td>
                  <td className="text-end fw-semibold" style={{ color: "#b97007" }}>{toFixed(row.tax)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid rgba(18, 125, 255, 0.2)" }}>
                <td colSpan="3" className="fw-bold">ภาษีนิติบุคคลทั้งปี</td>
                <td className="text-end fw-bold" style={{ color: "#b63636" }}>{toFixed(annual.cit?.tax)}</td>
              </tr>
              <tr>
                <td colSpan="3" className="fw-bold">หัก ภาษีที่ชำระตาม ภ.ง.ด.51</td>
                <td className="text-end fw-bold" style={{ color: "#188251" }}>-{toFixed(annual.pgd51?.tax)}</td>
              </tr>
              <tr>
                <td colSpan="3" className="fw-bold">หัก ภาษีที่ถูกหัก ณ ที่จ่ายไว้แล้ว</td>
                <td className="text-end fw-bold" style={{ color: "#188251" }}>-{toFixed(annual.wht_credit)}</td>
              </tr>
              <tr style={{ background: "rgba(12, 118, 229, 0.06)" }}>
                <td colSpan="3" className="fw-bold">คงเหลือที่ต้องชำระตอนยื่น ภ.ง.ด.50</td>
                <td className="text-end fw-bold" style={{ color: "#b63636" }}>{toFixed(annual.pgd50?.tax)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="small text-secondary mt-3">
          ครึ่งปีแรกมีกำไร {toFixed(annual.half_year?.profit)} บาท ระบบประมาณกำไรทั้งปีเป็น {toFixed(annual.half_year?.projected_profit)} บาท
          จึงคิด ภ.ง.ด.51 เท่ากับครึ่งหนึ่งของภาษีที่ประมาณไว้
        </div>
      </div>

      <div className="row g-3">
        <FilingCard cell={annual.pgd51} onOpen={onOpenCell} />
        <FilingCard cell={annual.pgd50} onOpen={onOpenCell} />
      </div>
    </>
  );
}
